import {
  select, call, put, takeEvery, all,
} from 'redux-saga/effects';
import callAdapter, { callAdapterResponseError } from '@/utils/sagaHelper';
import selectors from '@/selectors';
import {
  findItemByID, findWithCodes, findWithReadStatus, successAndErrorsOnDestroy,
} from '@/utils/resultsHelper';
import { states } from '@/constants/lifecycle';
import { downloadEndpointDatasetAttachments } from '@/constants/urls';
import downloads from '@/utils/downloads';
import findDataset from '../../state/shared';
import { datasetLists } from '../../constants';
import DatasetAttachmentsService from '../service';
import {
  createDatasetFolderError,
  createDatasetFolderSuccess,
  datasetAttachmentDownloadError,
  datasetAttachmentDownloadSuccess,
  deleteDatasetAttachmentError,
  deleteDatasetAttachmentSuccess,
  showDatasetAttachmentDictionary,
  showDatasetAttachmentDictionaryError,
  showDatasetAttachments,
  showDatasetAttachmentsError,
  updateDatasetAttachmentDescriptionError,
  updateDatasetAttachmentDescriptionSuccess,
  updateDatasetAttachmentMetadataError,
  updateDatasetAttachmentMetadataSuccess,
} from '../actions';
import actions from '../constants';
import { resolveCurrentPath } from '../helper';

export function* datasetAttachments(action) {
  const datasets = yield select(selectors.datasets);
  const item = findDataset({ datasets }, action);
  const path = resolveCurrentPath(item.attachments.folders.currentPathSegments);

  const { response, error } = yield call(
    callAdapter,
    DatasetAttachmentsService.datasetAttachments(action.code, undefined, undefined, path),
  );
  if (error) {
    yield put(showDatasetAttachmentsError(action.category, action.code, error));
  } else {
    yield put(showDatasetAttachments(action.category, action.code, response));
  }
}

export function* download(action) {
  const { error } = yield call(
    callAdapter,
    DatasetAttachmentsService.prepareDownload(action.code, action.attachment.id),
  );
  if (error) {
    yield put(datasetAttachmentDownloadError(action.category, action.code, action.attachment));

    return;
  }

  yield put(datasetAttachmentDownloadSuccess(action.category, action.code, action.attachment));

  downloads.startDownload(`/api${downloadEndpointDatasetAttachments(action.code, action.attachment.id)}`);
}

export function* updateAttachmentMetadata({
  code, category, attachmentId, metadata,
}) {
  const { response, error } = yield call(
    callAdapter,
    DatasetAttachmentsService.updateMetadata(code, attachmentId, metadata),
  );

  const datasets = yield select(selectors.datasets);
  const item = findDataset({ datasets }, { category, code });

  if (!item) {
    return;
  }

  if (error) {
    const attachment = findItemByID(item.attachments.items, { id: attachmentId });

    yield put(updateDatasetAttachmentMetadataError(category, code, attachmentId, error, attachment.name));
  } else {
    yield put(updateDatasetAttachmentMetadataSuccess(category, code, attachmentId, response));
  }
}

export function* destroyAttachment(action) {
  const results = yield all(action.items.map((i) => callAdapterResponseError(i, DatasetAttachmentsService.destroy(i))));
  const { success, errors } = successAndErrorsOnDestroy(results, 'Dataset attachment not found');

  if (errors.length > 0) {
    yield put(deleteDatasetAttachmentError(errors));
  }
  if (success.length > 0) {
    const items = yield select(selectors.datasets);
    const codes = success.map((item) => item.code);
    const categories = findWithCodes(items, datasetLists, codes);
    const toRefresh = findWithReadStatus(items, categories, states.SUCCESS);

    yield put(deleteDatasetAttachmentSuccess(success));
    yield all(toRefresh.map((category) => codes.map((code) => datasetAttachments({ category, code }))));
  }
}

export function* dictionaryFromAttachment(action) {
  const { response, error } = yield call(
    callAdapter,
    DatasetAttachmentsService.dictionary(action.code, action.attachment.id),
  );
  if (error) {
    yield put(showDatasetAttachmentDictionaryError(action.category, action.code, error));
  } else {
    yield put(showDatasetAttachmentDictionary(action.category, action.code, response));
  }
}

export function* updateAttachmentDescription({ category, code, attachment }) {
  const { response, error } = yield call(
    callAdapter,
    DatasetAttachmentsService.updateMetadata(code, attachment.id, { description: attachment.metadata.description }),
  );

  const datasets = yield select(selectors.datasets);
  const item = findDataset({ datasets }, { category, code });

  if (!item) {
    return;
  }

  if (error) {
    yield put(updateDatasetAttachmentDescriptionError(error));
  } else {
    yield put(updateDatasetAttachmentMetadataSuccess(category, code, attachment.id, response));
    yield put(updateDatasetAttachmentDescriptionSuccess(category, code, attachment));
  }
}

export function* createDatasetFolder(action) {
  const { category, code, folderName } = action;

  const datasets = yield select(selectors.datasets);
  const item = findDataset({ datasets }, { category, code });
  if (!item) return;
  const path = resolveCurrentPath(item.attachments.folders.currentPathSegments) + folderName;

  const { response, error } = yield call(
    callAdapter,
    DatasetAttachmentsService.createFolder(code, path),
  );

  if (error) {
    yield put(createDatasetFolderError(category, code, error));
  } else {
    yield put(createDatasetFolderSuccess(category, code, response));
  }
}

export function* deleteDatasetFolder(action) {
  yield destroyAttachment({ items: [action.folder] });
}

export default function* datasetAttachmentsApi() {
  yield takeEvery([
    actions.FETCH_DATASET_ATTACHMENTS,
    actions.DATASET_ATTACHMENT_FOLDER_PUSH,
    actions.DATASET_ATTACHMENT_FOLDER_NAVIGATE,
  ], datasetAttachments);
  yield takeEvery(actions.DATASET_ATTACHMENT_DOWNLOAD, download);
  yield takeEvery(actions.UPDATE_DATASET_ATTACHMENT_METADATA, updateAttachmentMetadata);
  yield takeEvery(actions.UPDATE_DATASET_ATTACHMENT_DESCRIPTION, updateAttachmentDescription);
  yield takeEvery(actions.DELETE_DATASET_ATTACHMENT, destroyAttachment);
  yield takeEvery(actions.SHOW_CONVERT_ATTACHMENT_DIALOG, dictionaryFromAttachment);
  yield takeEvery(actions.CREATE_DATASET_ATTACHMENT_FOLDER, createDatasetFolder);
  yield takeEvery(actions.DELETE_DATASET_ATTACHMENT_FOLDER, deleteDatasetFolder);
}
